import React, { useState } from 'react';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

export interface FormSectionProps {
  /** Icon displayed in a primary-colored container */
  icon?: IconName;
  /** Section title */
  title: string;
  /** Description text below title */
  description?: string;
  /** Section content */
  children: React.ReactNode;
  /** Allow collapsing the section */
  collapsible?: boolean;
  /** Initial collapsed state (only when collapsible) */
  defaultCollapsed?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Shared FormSection component that wraps form fields in a themed card
 * with an icon, title, and optional description.
 *
 * Eliminates the repeated pattern of card + icon container + title + description
 * found in 20+ forms across the codebase.
 *
 * @example
 * ```tsx
 * <FormSection
 *   icon={FiUser}
 *   title="Informações Básicas"
 *   description="Dados principais do registro"
 * >
 *   <FormField label="Nome">
 *     <Input value={name} onChange={...} />
 *   </FormField>
 * </FormSection>
 * ```
 */
export const FormSection: React.FC<FormSectionProps> = ({
  icon,
  title,
  description,
  children,
  collapsible = false,
  defaultCollapsed = false,
  className = '',
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  return (
    <div
      className={`rounded-lg border overflow-hidden ${className}`}
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Section Header */}
      <div
        className={`flex items-start gap-[11px] py-[15px] px-[18px] ${collapsible ? 'cursor-pointer select-none' : ''} ${isCollapsed ? '' : 'border-b'}`}
        style={isCollapsed ? undefined : { borderColor: 'var(--theme-card-border)' }}
        onClick={collapsible ? () => setIsCollapsed(!isCollapsed) : undefined}
      >
        {icon && (
          <div
            className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: 'var(--theme-primary-background)',
            }}
          >
            <Icon
              name={icon}
              className="w-4 h-4"
              style={{ color: 'var(--theme-primary)' }}
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h2 className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--theme-card-text)' }}>
            {title}
          </h2>
          {description && (
            <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
              {description}
            </p>
          )}
        </div>
        {collapsible && (
          <Icon
            name="chevron-down"
            className="w-5 h-5 flex-shrink-0 transition-transform"
            style={{
              color: 'var(--theme-muted-text)',
              transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            }}
          />
        )}
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="space-y-4 p-[18px]">
          {children}
        </div>
      )}
    </div>
  );
};

FormSection.displayName = 'FormSection';
